import { useEffect, useState } from 'react';

const FONT = "'DM Sans', sans-serif";
const FONT_DISPLAY = "'Source Serif 4', 'DM Sans', serif";

type Props = {
  slug: string;
  selectionString?: string;
  plan?: 'musica' | 'video';
  valor: number;
  cupomCodigo?: string | null;
};

type PixData = {
  qr_code_image: string | null;
  qr_code_payload: string;
};

export default function PixTab({ slug, selectionString = '', plan, valor, cupomCodigo }: Props) {
  const [data, setData] = useState<PixData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);

  // Toda vez que a seleção ou cupom muda, regera (invalida QR antigo se valor mudou).
  // O cleanup do effect anterior dispara cancelled=true antes do novo rodar,
  // então não há race entre fetches paralelos.
  useEffect(() => {
    if (!slug || (!selectionString && !plan)) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);

    fetch('/api/checkout?action=pix', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug, previas_selecionadas: selectionString || undefined, plano: plan, cupom_codigo: cupomCodigo || '' }),
    })
      .then(async (r) => {
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(d.error || 'Falha ao gerar PIX');
        }
        return r.json();
      })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, selectionString, plan, cupomCodigo]);

  const handleCopiar = async () => {
    if (!data?.qr_code_payload) return;
    try {
      await navigator.clipboard.writeText(data.qr_code_payload);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2400);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = data.qr_code_payload;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2400);
    }
  };

  if (loading && !data) {
    return (
      <div style={{ textAlign: 'center', padding: '36px 12px', fontFamily: FONT }}>
        <div
          aria-hidden
          style={{
            width: 26,
            height: 26,
            margin: '0 auto 14px',
            border: '2.5px solid var(--border-fade)',
            borderTopColor: 'var(--gold)',
            borderRadius: '50%',
            animation: 'inspiraSpin 0.85s linear infinite',
          }}
        />
        <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: 0 }}>Gerando seu PIX…</p>
        <style>{`@keyframes inspiraSpin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: '20px 16px',
          borderRadius: 12,
          background: 'rgba(214,76,43,0.08)',
          color: '#9F2A1F',
          fontSize: 14,
          textAlign: 'center',
          fontFamily: FONT,
        }}
      >
        {error}
      </div>
    );
  }

  if (!data) return null;

  return (
    <div style={{ fontFamily: FONT, animation: 'inspiraFadeIn 0.4s' }}>
      {/* Botão copia-cola (PIX Copia e Cola — preferido em desktop) */}
      <button
        type="button"
        onClick={handleCopiar}
        style={{
          width: '100%',
          padding: '16px 22px',
          borderRadius: 999,
          background: copiado ? '#1f8a5b' : 'var(--gold)',
          color: '#fff',
          fontWeight: 700,
          fontSize: 15.5,
          fontFamily: FONT,
          border: 'none',
          cursor: 'pointer',
          boxShadow: copiado ? '0 6px 22px rgba(31,138,91,0.35)' : '0 8px 28px rgba(255,145,77,0.42)',
          transition: 'background 0.22s, box-shadow 0.22s, transform 0.18s',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          letterSpacing: '-0.005em',
        }}
        onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.98)')}
        onMouseUp={(e) => (e.currentTarget.style.transform = '')}
        onMouseLeave={(e) => (e.currentTarget.style.transform = '')}
      >
        {copiado ? (
          <>
            <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 7.5L6 12.5L17 1.5" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Código copiado
          </>
        ) : (
          <>Copiar código PIX</>
        )}
      </button>

      <p
        style={{
          fontSize: 12.5,
          color: 'var(--text-muted)',
          textAlign: 'center',
          marginTop: 10,
          marginBottom: 16,
          lineHeight: 1.55,
        }}
      >
        Cole no <strong>Pix Copia e Cola</strong> do seu app de banco
      </p>

      {/* Divisor "OU" */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          margin: '18px 0',
          color: 'var(--text-soft)',
        }}
      >
        <div style={{ flex: 1, height: 1, background: 'var(--border-fade)' }} />
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase' }}>ou</span>
        <div style={{ flex: 1, height: 1, background: 'var(--border-fade)' }} />
      </div>

      {/* Card branco com QR Code (alternativa pra ler com câmera) */}
      {data.qr_code_image && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '20px 16px',
            background: '#fff',
            borderRadius: 18,
            border: '1px solid var(--border-soft)',
            marginBottom: 16,
          }}
        >
          <img
            src={data.qr_code_image}
            alt="QR Code para pagar via PIX"
            style={{
              width: 196,
              height: 196,
              borderRadius: 12,
              imageRendering: 'pixelated',
            }}
          />
          <p
            style={{
              marginTop: 12,
              marginBottom: 0,
              fontSize: 12,
              color: '#666',
              textAlign: 'center',
              fontWeight: 500,
              letterSpacing: '0.04em',
            }}
          >
            Aponte a câmera do seu app de banco
          </p>
        </div>
      )}

      {/* Valor + nota de confirmação */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 16px',
          borderRadius: 14,
          background: 'var(--section-alt)',
          fontSize: 13,
          color: 'var(--text-body)',
        }}
      >
        <span style={{ fontWeight: 500 }}>
          Total: <strong style={{ fontFamily: FONT_DISPLAY, fontStyle: 'italic', fontSize: 16, color: 'var(--heading-ink)' }}>R$ {valor.toFixed(2).replace('.', ',')}</strong>
        </span>
        <span style={{ fontSize: 11.5, color: 'var(--text-soft)', fontStyle: 'italic' }}>
          confirmamos em segundos
        </span>
      </div>

      <style>{`
        @keyframes inspiraFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
